/**
 * Rich Text Element Editor
 * Uses FormBuilderWithLayout from content-kit - no custom inputs
 */

import * as React from 'react';
import { FormBuilderWithLayout, type FormLayoutConfig } from '@ai-content-hub/content-kit';
import type { ElementEditorProps } from '@ai-content-hub/content-kit';
import { richTextElementSchema, type RichTextElementData } from './schema';
import { normalizeImportedTiptapContent } from './importNormalize';
import { normalizeRichTextMediaPaths } from './normalizeMediaPaths';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';
import { Button, markdownToTiptap } from '@ai-content-hub/content-kit';
import { getMarkdownString } from './detect';

export interface RichTextEditProps extends ElementEditorProps<RichTextElementData> {}

// Layout configuration for rich text element
const richTextLayout: FormLayoutConfig = {
    type: 'vertical',
    columns: 1,
};

export const RichTextEdit: React.FC<RichTextEditProps> = ({
    value,
    onChange,
    errors,
    disabled,
    className,
    config,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? richTextElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });

    const localizedSchema = React.useMemo(() => {
        return {
            ...schema,
            fields: schema.fields.map((field) =>
                field.name === 'content' && field.type === 'rich-text'
                    ? {
                          ...field,
                          label: t('content'),
                          description: "",
                      }
                    : field
            ),
        };
    }, [schema, t]);

    const importNormalizeSourceRef = React.useRef<string | null>(null);

    // Auto-convert { markdown: string } content to Tiptap JSON on first load
    React.useEffect(() => {
        if (!value?.content || disabled) {
            return;
        }
        const mdStr = getMarkdownString(value.content);
        if (mdStr != null && mdStr.trim() !== '') {
            const tiptap = markdownToTiptap(mdStr);
            onChange({ ...value, content: tiptap });
            return;
        }
    }, [value, onChange, disabled]);

    React.useEffect(() => {
        if (!value?.content || disabled) {
            return;
        }
        const mediaNormalized = normalizeRichTextMediaPaths(value.content);
        if (mediaNormalized != null) {
            onChange({ ...value, content: mediaNormalized });
            return;
        }
    }, [value, onChange, disabled]);

    React.useEffect(() => {
        if (!value?.content || disabled) {
            return;
        }
        const rawFp = JSON.stringify(value.content);
        const normalized = normalizeImportedTiptapContent(value.content);
        if (normalized == null) {
            return;
        }
        const nextFp = JSON.stringify(normalized);
        if (nextFp === rawFp) {
            return;
        }
        if (importNormalizeSourceRef.current === rawFp) {
            return;
        }
        importNormalizeSourceRef.current = rawFp;
        onChange({ ...value, content: normalized });
    }, [value, onChange, disabled]);

    return (
        <FormBuilderWithLayout
            schema={localizedSchema}
            value={value}
            onChange={onChange}
            layout={richTextLayout}
            errors={errors}
            disabled={disabled}
            className={className}
        />
    );
};

export default RichTextEdit;
